import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const CATEGORIES = ["accident", "safeguarding", "near_miss", "behaviour", "injury", "fall", "medication", "other"];
const SEVERITIES = ["low", "moderate", "high", "critical"];
const EMERGENCY_OPTS = ["ambulance", "police", "fire", "gp_out_of_hours"];

const initial = () => ({
  occurred_at: new Date().toISOString().slice(0, 16),
  category: "accident",
  severity: "low",
  client_id: "",
  client_name: "",
  location: "",
  reported_by: "",
  witnesses: [],
  description: "",
  immediate_action: "",
  injury_harm: "",
  medical_treatment_required: false,
  emergency_services: [],
});

export default function IncidentNewDialog({ open, onOpenChange, onCreated, clients }) {
  const [values, setValues] = useState(initial());
  const [saving, setSaving] = useState(false);
  const [witnessInput, setWitnessInput] = useState("");

  useEffect(() => {
    if (open) setValues(initial());
  }, [open]);

  const patch = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const addWitness = () => {
    const w = witnessInput.trim();
    if (!w) return;
    patch("witnesses", [...(values.witnesses || []), w]);
    setWitnessInput("");
  };

  const toggleEmergency = (opt) => {
    const cur = values.emergency_services || [];
    patch("emergency_services", cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt]);
  };

  const submit = async () => {
    if (!values.description?.trim()) return toast.error("Description is required");
    setSaving(true);
    try {
      const body = { ...values };
      if (body.client_id) {
        const c = clients?.find((x) => x.id === body.client_id);
        if (c && !body.client_name) body.client_name = c.full_name;
      }
      const r = await api.post("/incidents", body);
      onCreated?.(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto" data-testid="incident-new-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Record New Incident</DialogTitle>
          <DialogDescription>Capture the facts now. Investigation, actions and safeguarding can be added later.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Date &amp; Time *">
            <Input type="datetime-local" value={values.occurred_at} onChange={(e) => patch("occurred_at", e.target.value)} data-testid="input-occurred-at" />
          </Field>
          <Field label="Category *">
            <Select value={values.category} onChange={(v) => patch("category", v)} options={CATEGORIES} testid="input-category" />
          </Field>
          <Field label="Severity *">
            <Select value={values.severity} onChange={(v) => patch("severity", v)} options={SEVERITIES} testid="input-severity" />
          </Field>
          <Field label="Service User">
            <select
              value={values.client_id}
              onChange={(e) => patch("client_id", e.target.value)}
              className="h-9 w-full text-sm border border-slate-300 rounded-md bg-white px-2"
              data-testid="input-client"
            >
              <option value="">— None —</option>
              {(clients || []).map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
            </select>
          </Field>
          <Field label="Location">
            <Input value={values.location} onChange={(e) => patch("location", e.target.value)} data-testid="input-location" placeholder="e.g. Bedroom 4, kitchen…" />
          </Field>
          <Field label="Reported by">
            <Input value={values.reported_by} onChange={(e) => patch("reported_by", e.target.value)} data-testid="input-reported-by" placeholder="Full name" />
          </Field>
          <div className="col-span-2">
            <Field label="Witnesses">
              <div className="flex gap-2 items-center">
                <Input
                  value={witnessInput}
                  onChange={(e) => setWitnessInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addWitness(); } }}
                  data-testid="input-witness"
                  placeholder="Enter a name and press Enter"
                />
                <Button type="button" size="sm" variant="outline" onClick={addWitness} data-testid="btn-add-witness">Add</Button>
              </div>
              {(values.witnesses || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {values.witnesses.map((w, i) => (
                    <span key={i} className="pill pill-info">
                      {w}
                      <button
                        onClick={() => patch("witnesses", values.witnesses.filter((_, ix) => ix !== i))}
                        className="ml-1 text-slate-500 hover:text-red-600" title="Remove"
                      >×</button>
                    </span>
                  ))}
                </div>
              )}
            </Field>
          </div>
          <div className="col-span-2">
            <Field label="Detailed description *">
              <Textarea rows={4} value={values.description} onChange={(e) => patch("description", e.target.value)} data-testid="input-description" placeholder="What happened, when and where. Stick to facts." />
            </Field>
          </div>
          <div className="col-span-2">
            <Field label="Immediate action taken">
              <Textarea rows={3} value={values.immediate_action} onChange={(e) => patch("immediate_action", e.target.value)} data-testid="input-immediate-action" placeholder="Any first-aid, isolation, or containment?" />
            </Field>
          </div>
          <div className="col-span-2">
            <Field label="Injury / harm">
              <Textarea rows={2} value={values.injury_harm} onChange={(e) => patch("injury_harm", e.target.value)} data-testid="input-injury-harm" placeholder="Describe any injury or harm observed." />
            </Field>
          </div>
          <Field label="Medical treatment required?">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={!!values.medical_treatment_required} onChange={(e) => patch("medical_treatment_required", e.target.checked)} data-testid="input-medical-treatment" />
              Yes, medical attention was needed
            </label>
          </Field>
          <Field label="Emergency services involved">
            <div className="flex flex-wrap gap-3">
              {EMERGENCY_OPTS.map((o) => (
                <label key={o} className="flex items-center gap-1.5 text-sm text-slate-700 capitalize">
                  <input type="checkbox" checked={(values.emergency_services || []).includes(o)} onChange={() => toggleEmergency(o)} data-testid={`input-emergency-${o}`} />
                  {o.replace(/_/g, " ")}
                </label>
              ))}
            </div>
          </Field>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="btn-cancel-new">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="btn-save-new" className="bg-sky-700 hover:bg-sky-800">
            {saving ? "Saving…" : "Record Incident"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <div className="mt-0.5">{children}</div>
    </label>
  );
}

function Select({ value, onChange, options, testid }) {
  return (
    <select
      value={value} onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="h-9 w-full text-sm border border-slate-300 rounded-md bg-white px-2 capitalize"
    >
      {options.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
    </select>
  );
}
